import React, { useState, useEffect } from "react";
import { useParams, Navigate, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import CampaignDetailPage from "./index";
import MedicalCampaignDetailPage from "./MedicalCampaignDetailPage";
import TempleDetailPage from "../TempleDetailPage";
import ClosedCampaign from "../../components/common/CampaignDetailPageComponents/ClosedCampaign";
import { getCampaignBySlug, getCampaignById } from "../../api/services/campaignService";
import {
  getCampaignUrl,
  normalizeSlug,
  getLifePrimarySlugFromRedirects,
} from "../../utils/slugUtils";
import { canViewPrivateCampaign } from "../../utils/authUtils";

// A campaign is a temple campaign when "Temples" is one of its categories. The API sends the
// category list as a string like '["Urgent,Temples"]'.
const isTempleCampaign = (campaign) =>
  String(campaign?.category || "")
    .toLowerCase()
    .split(/[^a-z]+/)
    .some((word) => word === "temples" || word === "temple");

const CampaignDetailWrapper = () => {
  const { slug } = useParams();
  const location = useLocation();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState(null);
  
  // Get user and authentication state from Redux
  const { user, isAuthenticated } = useSelector((state) => state.auth);

  // Check if slug is a numeric ID (old format)
  const isNumericId = /^\d+$/.test(slug);

  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        setLoading(true);
        setCampaign(null);
        setError(null);
        setShouldRedirect(false);
        setRedirectUrl(null);
        
        // Campaign mapping: "Know more" sends campaignID in state. That ID is the mapped campaign (normal or life).
        const mappedCampaignId = location.state?.mappedCampaignId;
        const isCampaignRoute = location.pathname.startsWith("/campaign/");
        if (isCampaignRoute && mappedCampaignId != null && slug) {
          const response = await getCampaignById(mappedCampaignId);
          if (response?.data) {
            const mapped = response.data;
            // Life campaigns use /life/:slug, not /campaign/
            if (
              Number(mapped.featureLifeCampaign) === 1 ||
              Number(mapped.featuremedicalCampaign) === 1
            ) {
              const lifeSlug = getLifePrimarySlugFromRedirects(mapped, slug);
              setRedirectUrl(`/life/${lifeSlug}`);
              setShouldRedirect(true);
              setLoading(false);
              return;
            }
            setCampaign({
              ...mapped,
              redirects: [{ primary_url: slug, is_story_campaign: false }],
            });
            setLoading(false);
            return;
          }
        }
        
        // If slug is numeric, it's an old ID format - fetch by ID and redirect
        if (isNumericId) {
          const response = await getCampaignById(slug);
          if (response && response.data) {
            const campaignData = response.data;
            // Check if campaign has redirects data
            if (!campaignData.redirects || !Array.isArray(campaignData.redirects) || campaignData.redirects.length === 0) {
              setError(new Error("Campaign not found - no redirects data"));
              setLoading(false);
              return;
            }
            const url = getCampaignUrl(campaignData);
            if (!url) {
              setError(new Error("Campaign not found - invalid redirects"));
              setLoading(false);
              return;
            }
            setRedirectUrl(url);
            setShouldRedirect(true);
            setLoading(false);
            return;
          } else {
            setError(new Error("Campaign not found"));
            setLoading(false);
            return;
          }
        }
        
        // Otherwise, fetch by slug (new format). Pass route type so story and campaign with same slug resolve correctly.
        const isStoryRoute = location.pathname.startsWith("/story/");
        let response = null;
        try {
          response = await getCampaignBySlug(slug, isStoryRoute);
        } catch (slugError) {
          throw slugError;
        }
        
        // Service returns { data: {...} }
        if (response && response.data) {
          setCampaign(response.data);
          setError(null);
        } else {
          setCampaign(null);
          setError(new Error("Campaign not found"));
        }
      } catch (err) {
        setError(err);
        setCampaign(null);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchCampaign();
    }
  }, [slug, isNumericId, location.pathname, location.state?.mappedCampaignId]);

  // Show loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // If we need to redirect from old ID format
  if (shouldRedirect && redirectUrl) {
    return <Navigate to={redirectUrl} replace />;
  }

  // If campaign not found or error, navigate to 404 route (outside Layout)
  if (error || !campaign) {
    return <Navigate to="/404" state={{ message: "Campaign not found" }} replace />;
  }

  // Check if campaign is private and user has access
  if (campaign.IsPrivate === 1) {
    // Campaign is private - check if user can view it
    if (!canViewPrivateCampaign(user, isAuthenticated)) {
      return <Navigate to="/404" state={{ message: "Campaign not found" }} replace />;
    }
  }

  // Check if campaign has redirects data - if not, navigate to 404
  if (!campaign.redirects || !Array.isArray(campaign.redirects) || campaign.redirects.length === 0) {
    return <Navigate to="/404" state={{ message: "Campaign not found" }} replace />;
  }

  const redirect = campaign.redirects[0];
  const normalizedSlug = normalizeSlug(slug);
  const primaryUrl = redirect.primary_url ? normalizeSlug(redirect.primary_url) : null;
  const secondaryUrl = redirect.secondary_url ? normalizeSlug(redirect.secondary_url) : null;
  const isStoryCampaign = redirect.is_story_campaign === true;
  const isStoryRoute = location.pathname.startsWith('/story/');
  const isCampaignRoute = location.pathname.startsWith('/campaign/');
  const secondaryUrlHide = redirect.secondaryUrlHide !== undefined ? redirect.secondaryUrlHide : 0;

  // Check if accessed URL is secondary URL
  if (secondaryUrl && normalizedSlug === secondaryUrl) {
    // If secondaryUrlHide is 0 (enabled), redirect to primary URL
    if (secondaryUrlHide === 0) {
      const primaryUrlPath = isStoryCampaign 
        ? `/story/${redirect.primary_url}` 
        : `/campaign/${redirect.primary_url}`;
      return <Navigate to={primaryUrlPath} replace />;
    } else {
      return <Navigate to="/404" state={{ message: "Campaign not found" }} replace />;
    }
  }

  // Check if accessed URL matches primary URL
  if (primaryUrl && normalizedSlug !== primaryUrl && normalizedSlug !== secondaryUrl) {
    // Slug doesn't match current campaign – e.g. navigated from story→campaign (stale story) or back from campaign→story (stale campaign). Show loading so effect can fetch the correct campaign for this URL.
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  // Stale state: route and campaign type don't match. Show loading so effect can refetch for the current URL (avoids redirect loop when pressing back or after "Know more").
  // Case A: On /campaign/ with mappedCampaignId but state still has story (effect not run yet) → show loading, effect will set campaign.
  const fromKnowMore = location.state?.mappedCampaignId != null && isCampaignRoute;
  if (fromKnowMore && isStoryCampaign) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }
  // Case B: On /story/ but state has campaign (e.g. user pressed back from campaign) → show loading, effect will refetch and set story.
  if (isStoryRoute && !isStoryCampaign) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }
  // Story on /campaign/ (no Know more in progress) → redirect to /story/
  if (isStoryCampaign && !isStoryRoute) {
    const correctUrl = getCampaignUrl(campaign);
    if (correctUrl) {
      return <Navigate to={correctUrl} replace />;
    }
  }

  // Check if campaign is closed (Closedcampaign === 1)
  // If closed, show closed campaign page for both normal and medical campaigns
  if (campaign.Closedcampaign === 1) {
    return <ClosedCampaign campaignData={campaign} />;
  }

  // Check campaign_type: true = story/medical campaign, false = normal campaign
  // Pass campaign data to child components to avoid duplicate fetching
  if (isStoryCampaign) {
    return <MedicalCampaignDetailPage campaign={campaign} />;
  }

  // Temple campaigns get the temple detail page (Figma 3408:13879)
  if (isTempleCampaign(campaign)) {
    return <TempleDetailPage campaign={campaign} />;
  }

  // Default to regular campaign detail page
  return <CampaignDetailPage campaign={campaign} />;
};

export default CampaignDetailWrapper;
